import React, { useState } from 'react'
import { Dropdown, Menu, Icon, Popover, Spin } from 'antd'
import { RouteComponentProps, withRouter } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { ApplicationState } from 'src/store'
import { generateQRCode } from 'src/core/store/ducks/me/actions'
import { Image, MenuItem } from 'src/components/Ant'
import { Text } from 'src/components/atoms'
import images from 'src/config/images'
import LanguagePicker from 'src/components/LanguagePicker'
import UserAvatar from 'src/components/UserAvatar'
import * as CSS from './styles'
import EditUserModal from './components/edit-user-modal'
import ModalChangePassword from './components/modal-change-password'
import ModalExportOfExpenses from './components/modal-export-of-expenses'
import ModalIntegration from './components/modal-integration'
import ModalIntegrationUpdate from './components/modal-integration-update'
import ReasonModal from './components/ReasonModal'

function Side({ history, match }: RouteComponentProps) {
  const {
    general,
    me: {
      loading,
      organization: { tenant },
      qrCode,
      user: {
        auditor,
        dashboard,
        first_name,
        gender,
        integrator,
        last_name,
        master_key,
        role_group,
        organization_id,
        tenant_id,
        user_id,
        user_level_type
      }
    }
  } = useSelector((state: ApplicationState) => ({
    general: state.general,
    me: state.me
  }))

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [editUserVisible, setEditUserVisible] = useState<boolean>(false)
  const [reasonModalVisible, setReasonModalVisible] = useState<boolean>(false)
  const [modalChangePasswordVisible, setModalChangePasswordVisible] =
    useState<boolean>(false)
  const [modalIntegrationVisible, setModalIntegrationVisible] =
    useState<boolean>(false)
  const [modalExportOfExpensesVisible, setModalExportOfExpensesVisible] =
    useState<boolean>(false)
  const [modalIntegrationUpdateVisible, setModalIntegrationUpdateVisible] =
    useState<boolean>(false)

  const isAdmin = ['ADMIN', 'REALM_ADMIN', 'SUPER_ADMIN'].includes(
    user_level_type
  )

  const fullName = `${first_name} ${last_name}`
  const isAgency = tenant?.tenant_type === 'AGENCY'
  const isManager = role_group.manager.length > 0

  const shouldDisplayExportR142 =
    [597990758089, 150044612598].includes(+tenant_id) ||
    (+tenant_id === 559042084949 && +user_id === 5871)

  const shouldDisplayUpdateSyncStatus =
    [597990758089, 294931374904].includes(+tenant_id) && (isAdmin || auditor)
  const canCreateTenant = [597990758089].includes(+tenant_id)
  const shouldDisplayNewRequest = !(
    [559042084949].includes(+tenant_id) && ![5819, 5871].includes(+user_id)
  )

  function RenderMenus() {
    if (isAgency) {
      return (
        <CSS.SideMenu
          defaultOpenKeys={[match.path]}
          onClick={({ key }: any) => {
            history.push(key)
          }}
        >
          <CSS.SubMenu
            key="agencies"
            icon="book"
            title="web.side.agencies.title"
          >
            <MenuItem
              key="/agencies/issuances"
              title="web.side.agencies.issuances"
            />
            <MenuItem
              key="/agencies/partners"
              title="web.side.agencies.partners"
            />
            <MenuItem
              key="/agencies/agreements"
              title="web.side.agencies.agreements"
            />
          </CSS.SubMenu>
        </CSS.SideMenu>
      )
    }

    return (
      <CSS.SideMenu
        defaultOpenKeys={[match.path]}
        onClick={({ key }: any) => {
          switch (key) {
            case 'newRequest':
              setReasonModalVisible(true)
              break
            default:
              history.push(key)
          }
        }}
      >
        {shouldDisplayNewRequest && (
          <MenuItem
            data-test-id="dashboard-new-request"
            icon="plus"
            key="newRequest"
            title="web.side.new-request"
          />
        )}
        <CSS.SubMenu
          data-test-id="dashboard-requests-item"
          icon="unordered-list"
          key="requests"
          title="web.side.request-list"
        >
          <MenuItem key="/my-requests" title="web.side.my-requests" />
          {auditor && <MenuItem key="/audits" title="web.side.audits" />}
          {!!role_group.approver.length && (
            <MenuItem key="/approvals" title="web.side.approvals" />
          )}
          {!!role_group.checker.length && (
            <MenuItem key="/conferences" title="web.side.conferences" />
          )}
          {!!role_group.controller.length && (
            <MenuItem key="/closings" title="web.side.closings" />
          )}
        </CSS.SubMenu>
        {(isAdmin || isManager) && (
          <CSS.SubMenu
            data-test-id="dashboard-management-list"
            icon="appstore"
            key="management"
            title="web.side.management.title"
          >
            {isAdmin && (
              <CSS.SubMenu
                data-test-id="dashboard-administration-list"
                key="administration"
                title="web.side.management.administration.title"
              >
                <CSS.SubMenu
                  data-test-id="dashboard-resources-list"
                  key="resources"
                  title="web.side.management.administration.resources"
                >
                  <MenuItem
                    key="/management/administration/resources/billing_types"
                    title="web.resources.billing-types.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/contracts"
                    title="web.resources.contracts.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/cost_centers"
                    title="web.resources.cost-centers.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/currencies"
                    title="web.resources.currencies.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/financial_resources"
                    title="web.resources.financial-resources.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/payment_resources"
                    title="web.resources.payment-resources.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/reasons"
                    title="web.resources.reasons.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/references"
                    title="web.resources.references.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/services"
                    title="web.resources.services.title-plural"
                  />
                  <MenuItem
                    key="/management/administration/resources/targets"
                    title="web.resources.targets.title-plural"
                  />
                </CSS.SubMenu>
                <MenuItem key="/users" title="web.user.title" />
                <MenuItem key="/management/tree" title="web.tree.title" />
                <MenuItem key="/policies" title="web.policies.title-plural" />
                <MenuItem
                  key="/management/flow"
                  title="web.policies-flow.title"
                />
                <MenuItem
                  key="/management/administration/antifraud"
                  title="web.anti-fraud.title"
                />
                <MenuItem
                  key="/management/administration/email/notifications"
                  title="web.user-email-notification.side-menu"
                />
              </CSS.SubMenu>
            )}
            {isManager && (
              <MenuItem key="/management/my-team" title="web.side.my-team" />
            )}
            {isManager && (
              <MenuItem key="/management/reports" title="web.side.reports" />
            )}
          </CSS.SubMenu>
        )}
        {(isAdmin || dashboard || isManager) && (
          <CSS.SubMenu
            data-test-id="dashboard"
            icon="pie-chart"
            key="chart"
            title="web.dashboard"
          >
            <MenuItem key="/dashboard" title="web.dashboard" />
          </CSS.SubMenu>
        )}
      </CSS.SideMenu>
    )
  }

  return (
    <>
      <CSS.Side
        className="no-print"
        collapsed={general.collapsedSide}
        collapsible
        trigger={null}
      >
        <CSS.LogoContainer>
          <CSS.LogoIcon
            alt="web.login.img.alt"
            src={images.logoIcon}
            title="web.login.img.title"
          />
          {!general.collapsedSide && (
            <CSS.LogoName
              alt="web.login.img.alt"
              src={images.logoName}
              title="web.login.img.title"
            />
          )}
        </CSS.LogoContainer>
        <RenderMenus />
      </CSS.Side>
      <CSS.LastButtons collapsed={!general.collapsedSide} className="no-print">
        <CSS.Language>
          <Dropdown
            overlay={
              <Menu
                onClick={() => {
                  history.push('/help/videos')
                }}
              >
                <MenuItem
                  aria-label="English"
                  data-test-id="dashboard-videos"
                  key="videos"
                >
                  {t('web.side.help.video')}
                </MenuItem>
              </Menu>
            }
            trigger={['hover']}
            placement="topRight"
          >
            <Icon type="question-circle" />
          </Dropdown>
        </CSS.Language>
        {!isAgency && (
          <div>
            <Popover
              autoAdjustOverflow
              content={
                <CSS.QRCodeContainer>
                  {loading.qrCode ? (
                    <CSS.Spin>
                      <Spin />
                    </CSS.Spin>
                  ) : (
                    <Image
                      data-test-id="dashboard-qrcode-image"
                      alt="web.side.qrcode"
                      src={qrCode}
                    />
                  )}
                </CSS.QRCodeContainer>
              }
              placement="rightBottom"
              trigger="click"
            >
              <CSS.SideMenuText
                data-test-id="dashboard-qrcode-list"
                onClick={() => dispatch(generateQRCode())}
              >
                <CSS.QRCodeIcon type="qrcode" />
                {!general.collapsedSide && <span>{t('web.side.qrcode')}</span>}
              </CSS.SideMenuText>
            </Popover>
          </div>
        )}
        <CSS.Language>
          <LanguagePicker showTheChosen={!general.collapsedSide} />
        </CSS.Language>
        <div>
          <Dropdown
            overlay={
              <>
                <CSS.Name>
                  <>
                    {fullName}
                    {!!master_key && (
                      <Text
                        copyable={{
                          text: master_key
                        }}
                      />
                    )}
                  </>
                </CSS.Name>
                <Menu
                  onClick={({ key }) => {
                    setEditUserVisible(false)
                    setModalChangePasswordVisible(false)
                    setModalIntegrationVisible(false)
                    switch (key) {
                      case 'logout':
                        localStorage.clear()
                        window.location.reload()
                        window.location.replace('/')
                        break
                      case 'change-password':
                        setModalChangePasswordVisible(true)
                        break
                      case 'integration':
                        setModalIntegrationVisible(true)
                        break
                      case 'export-of-expense':
                        setModalExportOfExpensesVisible(true)
                        break
                      case 'update-sync_status':
                        setModalIntegrationUpdateVisible(true)
                        break
                      case 'manage-tenant':
                        history.push('/tenant')
                        break
                      case 'edit':
                        setEditUserVisible(true)
                        break
                    }
                  }}
                >
                  {shouldDisplayExportR142 && <Menu.Divider />}
                  {shouldDisplayExportR142 && (
                    <MenuItem
                      data-test-id="export-of-expense"
                      key="export-of-expense"
                      title="web.side.export-of-expenses"
                    />
                  )}
                  {shouldDisplayUpdateSyncStatus && <Menu.Divider />}
                  {shouldDisplayUpdateSyncStatus && (
                    <MenuItem
                      data-test-id="update-sync_status"
                      key="update-sync_status"
                      title="web.integration.integration-adjustment"
                    />
                  )}
                  <Menu.Divider />
                  <MenuItem
                    data-test-id="dashboard-user-edit"
                    key="edit"
                    icon="edit"
                    title="common.actions.edit"
                  />
                  {canCreateTenant && <Menu.Divider />}
                  {canCreateTenant && (
                    <MenuItem
                      data-test-id="dashboard-manage-tenant"
                      key="manage-tenant"
                      title="web.side.tenant"
                    />
                  )}

                  <Menu.Divider />
                  <MenuItem
                    data-test-id="dashboard-change-password"
                    key="change-password"
                    icon="key"
                    title="web.side.user.change-password.title"
                  />
                  <Menu.Divider />
                  {integrator && (
                    <MenuItem
                      data-test-id="dashboard-interaction"
                      icon="interaction"
                      key="integration"
                      title="web.integration.title"
                    />
                  )}
                  {integrator && <Menu.Divider />}
                  <MenuItem
                    data-test-id="dashboard-logout"
                    key="logout"
                    icon="logout"
                    title="web.side.user.logout"
                  />
                </Menu>
              </>
            }
            trigger={['hover']}
            placement="topRight"
          >
            <UserAvatar
              data-test-id="dashboard-user"
              gender={gender}
              name={fullName}
            />
          </Dropdown>
        </div>
      </CSS.LastButtons>
      <ReasonModal
        toggle={() => setReasonModalVisible(!reasonModalVisible)}
        visible={reasonModalVisible}
      />
      <EditUserModal
        toggle={() => setEditUserVisible(!editUserVisible)}
        visible={editUserVisible}
      />
      <ModalChangePassword
        visible={modalChangePasswordVisible}
        toggle={() => {
          setModalChangePasswordVisible(false)
        }}
      />
      <ModalIntegration
        toggle={() => {
          setModalIntegrationVisible(false)
        }}
        visible={modalIntegrationVisible}
      />
      <ModalExportOfExpenses
        toggle={() => {
          setModalExportOfExpensesVisible(false)
        }}
        visible={modalExportOfExpensesVisible}
      />
      <ModalIntegrationUpdate
        toggle={() => {
          setModalIntegrationUpdateVisible(false)
        }}
        organizationId={organization_id}
        visible={modalIntegrationUpdateVisible}
      />
    </>
  )
}

export default withRouter(Side)
